import React, { ImgHTMLAttributes, forwardRef, useState } from 'react';
import { avatarColorForIndex, NEW_USER_AVATAR_COLOR } from './avatar_color';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'size'> {
  size?: AvatarSize;
  name?: string;
  src?: string;
  status?: 'online' | 'offline' | 'away' | 'busy';
  /** Deterministic palette index for the initials fallback (see avatar_color). */
  colorIndex?: number;
  /**
   * Render the neutral swatch instead of a palette colour — for someone who
   * has been given access but has not used it yet. Wins over `colorIndex`.
   */
  muted?: boolean;
}

const sizeClasses: Record<AvatarSize, string> = {
  xs: 'w-6 h-6 text-xs',
  sm: 'au-avatar-sm',
  md: 'au-avatar-md',
  lg: 'au-avatar-lg',
  xl: 'w-16 h-16 text-xl',
};

const statusClasses: Record<string, string> = {
  online: 'bg-au-success',
  offline: 'bg-au-text-disabled',
  away: 'bg-au-warning',
  busy: 'bg-au-danger',
};

const statusSizeClasses: Record<AvatarSize, string> = {
  xs: 'w-1.5 h-1.5 border',
  sm: 'w-2 h-2 border',
  md: 'w-2.5 h-2.5 border-2',
  lg: 'w-3 h-3 border-2',
  xl: 'w-3.5 h-3.5 border-2',
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ size = 'md', name, src, status, alt, className = '', colorIndex, muted, ...props }, ref) => {
    const [imageError, setImageError] = useState(false);
    const showFallback = !src || imageError;
    const initials = name ? getInitials(name) : '?';
    const paletteColor = (() => {
      if (!showFallback) return undefined;
      if (muted) return NEW_USER_AVATAR_COLOR;
      return colorIndex != null ? avatarColorForIndex(colorIndex) : undefined;
    })();

    return (
      <div ref={ref} className={`relative inline-block ${className}`}>
        <div
          className={`au-avatar ${sizeClasses[size]}`}
          style={
            paletteColor
              ? { backgroundColor: paletteColor.bg, color: paletteColor.text }
              : undefined
          }
        >
          {showFallback ? (
            <span className="select-none">{initials}</span>
          ) : (
            <img
              src={src}
              alt={alt || name || 'Avatar'}
              onError={() => setImageError(true)}
              className="w-full h-full object-cover"
              {...props}
            />
          )}
        </div>

        {status && (
          <span
            className={`
              absolute bottom-0 right-0
              ${statusSizeClasses[size]}
              ${statusClasses[status]}
              rounded-full
              border-au-surface
            `}
            aria-label={`Status: ${status}`}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

// Avatar Group component
interface AvatarGroupProps {
  children: React.ReactNode;
  max?: number;
  size?: AvatarSize;
}

/**
 * A stable key for one member of the group.
 *
 * `React.Children.toArray` keys every element it returns, derived from the
 * caller's own key when there is one, so the wrapper reuses that identity
 * instead of a position. A text child stands in for itself.
 */
const groupChildKey = (child: React.ReactNode): string =>
  React.isValidElement(child) ? String(child.key) : String(child);

const AvatarGroup: React.FC<AvatarGroupProps> = ({ children, max = 4, size = 'md' }) => {
  const childArray = React.Children.toArray(children);
  const visibleChildren = max ? childArray.slice(0, max) : childArray;
  const remainingCount = childArray.length - visibleChildren.length;

  return (
    <div className="flex -space-x-2">
      {visibleChildren.map((child) => (
        <div key={groupChildKey(child)} className="relative ring-2 ring-au-surface rounded-full">
          {React.isValidElement(child)
            ? React.cloneElement(child as React.ReactElement<AvatarProps>, { size })
            : child}
        </div>
      ))}
      {remainingCount > 0 && (
        <div
          className={`
            au-avatar ${sizeClasses[size]}
            bg-au-surface-sunken
            text-au-text-secondary
            ring-2 ring-au-surface
          `}
        >
          <span className="select-none">+{remainingCount}</span>
        </div>
      )}
    </div>
  );
};

export { Avatar, AvatarGroup };
export default Avatar;
